<script setup>
/**
 * The builder's chrome: back navigation, the inline campaign name, the
 * status chip, the autosave state label, and the Schedule/Publish actions.
 * Purely presentational — every action is emitted for the page to handle.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  /** Uppercase key into CAMPAIGN.BUILDER.STATUS, e.g. `DRAFT`. */
  statusKey: {
    type: String,
    required: true,
  },
  /** One of idle | saving | saved | error. */
  saveState: {
    type: String,
    default: 'idle',
  },
  canPublish: {
    type: Boolean,
    default: false,
  },
  isPublishing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['back', 'test', 'schedule', 'publish']);

const title = defineModel('title', { type: String, default: '' });

const { t } = useI18n();

const STATUS_PILL_CLASSES = {
  DRAFT: 'bg-n-alpha-2 text-n-slate-11',
  ACTIVE: 'bg-n-teal-3 text-n-teal-11',
  PROCESSING: 'bg-n-teal-3 text-n-teal-11',
  PAUSED: 'bg-n-amber-3 text-n-amber-11',
  SCHEDULED: 'bg-n-violet-3 text-n-violet-11',
  COMPLETED: 'bg-n-violet-3 text-n-violet-11',
};

const statusPillClasses = computed(
  () => STATUS_PILL_CLASSES[props.statusKey] ?? STATUS_PILL_CLASSES.DRAFT
);

const saveStateLabel = computed(() =>
  props.saveState === 'idle'
    ? ''
    : t(`CAMPAIGN.BUILDER.SAVE_STATE.${props.saveState.toUpperCase()}`)
);
</script>

<template>
  <header
    class="flex items-center flex-shrink-0 gap-3 px-4 border-b h-14 border-n-weak bg-n-solid-1"
  >
    <Button
      variant="ghost"
      color="slate"
      size="sm"
      icon="i-lucide-arrow-left"
      :aria-label="t('CAMPAIGN.BUILDER.BACK')"
      @click="emit('back')"
    />

    <div class="flex items-center flex-1 min-w-0 gap-2">
      <input
        v-model="title"
        type="text"
        :placeholder="t('CAMPAIGN.BUILDER.NAME_PLACEHOLDER')"
        :aria-label="t('CAMPAIGN.BUILDER.NAME_PLACEHOLDER')"
        class="min-w-0 max-w-xs w-full px-2 py-1 text-sm font-medium bg-transparent rounded-lg text-n-slate-12 placeholder:text-n-slate-10 hover:bg-n-alpha-1 focus:bg-n-alpha-1 focus:outline-none"
      />
      <span
        class="inline-flex items-center flex-shrink-0 h-5 px-2 font-medium rounded-full text-label-small"
        :class="statusPillClasses"
      >
        {{ t(`CAMPAIGN.BUILDER.STATUS.${statusKey}`) }}
      </span>
      <span
        v-if="saveStateLabel"
        class="flex-shrink-0 text-xs text-n-slate-10"
        aria-live="polite"
      >
        {{ saveStateLabel }}
      </span>
    </div>

    <div class="flex items-center flex-shrink-0 gap-2">
      <Button
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-flask-conical"
        :label="t('CAMPAIGN.BUILDER.TEST_PAGE')"
        @click="emit('test')"
      />
      <Button
        variant="faded"
        color="slate"
        size="sm"
        icon="i-lucide-calendar-clock"
        :label="t('CAMPAIGN.BUILDER.SCHEDULE')"
        @click="emit('schedule')"
      />
      <Button
        v-if="canPublish"
        color="violet"
        size="sm"
        icon="i-lucide-rocket"
        :label="t('CAMPAIGN.BUILDER.PUBLISH')"
        :is-loading="isPublishing"
        :disabled="isPublishing"
        @click="emit('publish')"
      />
    </div>
  </header>
</template>
